import { useMemo, useState, type DragEvent } from 'react';
import type { StreamChat } from 'stream-chat';

import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { useInboxList } from '../data/inbox.js';
import { listSummary } from '../util/format.js';
import { emptyQuery, withFilter } from './query.js';
import { ListBadge } from './ListBadge.js';
import { ListEditor } from './ListEditor.js';
import { isChatDayList, useLists, type ConversationList, type ListStyle } from './useLists.js';
import styles from './ListsPanel.module.css';

/** The loaded inbox a list's members are counted against: open, person-facing. */
const OPEN_INBOX_QUERY = withFilter(
  withFilter(emptyQuery(), { kind: 'status', phases: ['open'] }),
  { kind: 'kind', kinds: ['job', 'direct'] },
);

/**
 * The two kinds of list an employer ends up with, as the chips that tell them
 * apart: the ones they made, and the ones a Chat Day booking made for them.
 *
 * "All" and not "Mine", even though the other chip is the narrow one: the
 * booked lists are the employer's too — they are just not the ones the employer
 * sat down and built — so a chip that called the other half "Mine" would be
 * claiming a distinction the panel does not actually draw.
 */
const KINDS = [
  { value: 'all', label: 'All' },
  { value: 'chatDay', label: 'Chat Day' },
] as const;

type ListKind = (typeof KINDS)[number]['value'];

/**
 * Whether a drag is over the empty space under the last row, as opposed to one
 * of the strips the scrolling column also owns — above the first row, beside
 * any row, or the reserved scrollbar lane.
 *
 * Measured rather than inferred from the event target, because every one of
 * those strips reports the column itself as the target and they are not all
 * below anything.
 */
function belowLastRow(event: DragEvent<HTMLElement>): boolean {
  const rows = event.currentTarget.querySelectorAll('[data-list-row]');
  const last = rows[rows.length - 1];
  return last ? event.clientY > last.getBoundingClientRect().bottom : false;
}

/**
 * The rail's lists: every hand-picked set, by the mark it was given. Create,
 * dress, rename, delete, reorder, or open one back into the inbox (which scopes
 * to it).
 *
 * ponytail: name + a still-open count + chosen dress, not member faces — the
 * chip is the mark that holds still, and the count reads the loaded inbox page
 * only (following a list across the read model's pages is wiring, not this).
 */
export function ListsPanel({
  client,
  onOpen,
}: {
  client?: StreamChat | null;
  onOpen: (listId: number) => void;
}) {
  const { lists, loading, create, update, reorder, remove } = useLists();
  // Which chip is pressed. Component state, so it resets to All on every visit:
  // this is a way of looking at the column, not a setting somebody configured
  // and would expect to find still in force a week later.
  const [kind, setKind] = useState<ListKind>('all');
  // membersOf, ported: how many of each list's members are still in the loaded
  // (open) inbox. One Set over the page, then a count per list — kept behind a
  // memo because a drag frame or a pencil press re-renders without changing it.
  const inbox = useInboxList(OPEN_INBOX_QUERY, client ?? null);
  const openCounts = useMemo(() => {
    const loaded = new Set(inbox.items.map((item) => item.channel.id));
    return new Map(
      lists.map((list) => [list.id, list.channelIds.filter((id) => loaded.has(id)).length]),
    );
  }, [inbox.items, lists]);
  // What the column actually shows. Every read below that is about ROWS — the
  // drag maths, the grip, the map — goes through this and not `lists`, so a row
  // moves relative to the neighbours the employer can see. The counts and the
  // "no lists at all" empty state below stay on `lists`, because those are about
  // what is owned rather than what is on screen.
  const visible = useMemo(
    () => (kind === 'chatDay' ? lists.filter(isChatDayList) : lists),
    [kind, lists],
  );
  const chatDayCount = useMemo(() => lists.filter(isChatDayList).length, [lists]);
  // The one-time load, still in flight with nothing to show. The skeleton below
  // hangs off this, and so do the chips' counts: an empty store is not the
  // answer "you have none" yet, and a chip that says 0 over a skeleton is the
  // very claim the skeleton is there to avoid making.
  const firstLoad = loading && lists.length === 0;
  // Which list the editor is dressing: an existing one, or the new one about to
  // exist. One value, not a `creating` flag beside an `editId`, because the two
  // can never both be true and a pair that can disagree will.
  const [editing, setEditing] = useState<{ list: ConversationList | null } | null>(null);
  // The row being carried, and the one under the pointer. Two ids rather than
  // indices: the array is rewritten mid-drag by the drop itself, and an index
  // taken before that points at whatever moved into the slot.
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);

  const endDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  const dragIndex = dragId !== null ? visible.findIndex((list) => list.id === dragId) : -1;
  // Nothing to reorder with one list, so nothing offers to. A handle that can
  // only ever put the row back where it already is is an affordance for an
  // action that does not exist, and it appears on the very first list somebody
  // makes — the moment they have least idea what the panel does.
  const sortable = visible.length > 1;

  // The one place a list is dressed: a new list is saved by making it, an
  // existing one by writing over it. Nothing else differs, so one editor serves
  // both.
  const saveEdit = (style: ListStyle) => {
    if (editing?.list) update(editing.list.id, style);
    else create(style);
    setEditing(null);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Saved lists</h1>
        <button
          className={styles.newList}
          title="New list"
          // Back to All first: a list made by hand is never a Chat Day list, so
          // one started under that chip would be typed, named, saved — and then
          // vanish, into a column that is filtering it out. The chip is dropped
          // rather than the press refused, because the employer asked for a new
          // list and that is what they get.
          onClick={() => {
            setKind('all');
            setEditing({ list: null });
          }}
        >
          <Icon name="plus" size={20} />
        </button>
      </div>
      {/* The chips ride under the header rather than beside the title: they
          belong to the column they narrow, and the header's right-hand side is
          already the one place a new list is made. */}
      <div className={styles.kinds}>
        {KINDS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={option.value === kind ? styles.kindPillActive : styles.kindPill}
            aria-pressed={option.value === kind}
            onClick={() => setKind(option.value)}
          >
            {option.label}
            {/* Off the whole store, never off the filtered view: a count that
                  read the rows on screen would make "All" shrink to agree with
                  "Chat Day" the moment Chat Day was pressed, and both chips
                  would then report a number that is only true of one of them. */}
            {!firstLoad && (
              <span className={styles.kindCount}>
                {option.value === 'chatDay' ? chatDayCount : lists.length}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* The column is a drop target too, and it is the one that makes dragging
          downward work at all: moving a row below the last one means letting go
          in the empty space under the list, and with only the rows listening
          that release landed on nothing and the drag was silently abandoned.
       *
       * Only genuinely *below* the last row, though. The column also owns the
       * strip above the first row, the gutters either side of every row and the
       * reserved scrollbar lane — take the drop there and a row released above
       * the top of the list jumps to the bottom, which is the one place the
       * pointer was not. Anywhere else the release is simply refused. */}
      <div
        className={styles.list}
        aria-busy={loading || undefined}
        onDragOver={(event) => {
          if (dragId === null || !belowLastRow(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setOverId(visible[visible.length - 1]?.id ?? null);
        }}
        onDrop={(event) => {
          if (dragId === null || !belowLastRow(event)) return;
          event.preventDefault();
          const last = visible[visible.length - 1];
          if (last) reorder(dragId, last.id);
          endDrag();
        }}
      >
        {/* A new list is edited before it exists, in a row of its own above the
            others — the same editor an existing row turns into, so naming and
            dressing one is the same errand whichever way you came to it. */}
        {editing?.list === null && (
          <ListEditor
            list={null}
            summary={listSummary(0, 0)}
            onSave={saveEdit}
            onCancel={() => setEditing(null)}
          />
        )}

        {/* Four states: a skeleton while the one-time load is still in flight
            (so a not-yet-loaded store never reads as "No saved lists yet"); the
            empty state once loaded with nothing — but not mid-create, since only
            an in-flight creation suppresses it, so an edit with no lists still
            shows it; the chip's own empty line when the employer HAS lists and
            this chip matched none of them, which is a different sentence and has
            to be, since "No saved lists yet" would be a plain lie; otherwise the
            rows. */}
        {firstLoad ? (
          <div data-list-skeleton aria-hidden="true">
            {[0, 1, 2].map((row) => (
              <div key={row} className={styles.skeletonRow}>
                <span className={styles.skeletonBadge} />
                <div className={styles.skeletonLines}>
                  <span className={styles.skeletonName} />
                  <span className={styles.skeletonSummary} />
                </div>
              </div>
            ))}
          </div>
        ) : lists.length === 0 && editing?.list !== null ? (
          <EmptyState
            icon={<Icon name="bookmark" size={32} />}
            title="No saved lists yet"
            description="Start one above, or pick conversations in the inbox and add them to a list."
          />
        ) : visible.length === 0 && editing?.list !== null ? (
          <EmptyState
            icon={<Icon name="calendar" size={32} />}
            title="No Chat Day lists yet"
            description="Book a Chat Day and its guests are kept here as a list."
          />
        ) : (
          visible.map((list, index) => {
            const open = openCounts.get(list.id) ?? 0;
            // The row becomes the editor rather than opening one: same chip in
            // the same place, same two controls on the right.
            if (editing?.list?.id === list.id) {
              return (
                <ListEditor
                  key={list.id}
                  list={list}
                  summary={listSummary(list.channelIds.length, open)}
                  onSave={saveEdit}
                  onCancel={() => setEditing(null)}
                />
              );
            }

            const dragging = dragId === list.id;
            const over = overId === list.id && !dragging;
            // Which edge the line goes on. Dropping splices the carried row in
            // at this index, so coming from above it ends up after this row and
            // from below it ends up before — the line has to say which, or it
            // points at the wrong side on every downward drag.
            const landsAfter = over && dragIndex !== -1 && dragIndex < index;

            return (
              <div
                key={list.id}
                data-list-row=""
                className={[
                  styles.row,
                  dragging && styles.rowDragging,
                  over && (landsAfter ? styles.rowOverAfter : styles.rowOverBefore),
                ]
                  .filter(Boolean)
                  .join(' ')}
                draggable={sortable}
                onDragStart={(event) => {
                  setDragId(list.id);
                  // Firefox starts no drag at all without data on the transfer.
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', String(list.id));
                }}
                onDragEnd={endDrag}
                onDragOver={(event) => {
                  // The default is "no drop here", so without this the row never
                  // becomes a target and onDrop never fires. Held from the
                  // column behind it, which is the fallback target and would
                  // otherwise overwrite this row as the answer.
                  event.preventDefault();
                  event.stopPropagation();
                  event.dataTransfer.dropEffect = 'move';
                  setOverId(list.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (dragId !== null) reorder(dragId, list.id);
                  endDrag();
                }}
              >
                {/* The affordance, not the mechanism: the whole row is the drag
                    source, so grabbing anywhere works and this is what says so.
                    Absent below two lists, which also leaves the row at its
                    resting padding — the hover nudge hangs off this element.
                 *
                 * A button, and focusable, because drag is a pointer gesture and
                 * a keyboard has no way to perform one. The arrows move the row
                 * one place; every other control in this panel has a non-pointer
                 * route and this was the one that did not. */}
                {sortable && (
                  <button
                    type="button"
                    className={styles.grip}
                    aria-label={`Reorder ${list.name}. Use arrow keys to move it.`}
                    onKeyDown={(event) => {
                      const step = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
                      const target = visible[index + step];
                      if (step === 0 || !target) return;
                      // Otherwise the column scrolls out from under the row that
                      // is being moved, one press at a time.
                      event.preventDefault();
                      reorder(list.id, target.id);
                    }}
                  >
                    <Icon name="grip" size={16} />
                  </button>
                )}
                <ListBadge list={list} />
                <button className={styles.open} onClick={() => onOpen(list.id)}>
                  <span className={styles.name}>{list.name}</span>
                  <span className={styles.summary}>
                    {listSummary(list.channelIds.length, open)}
                  </span>
                </button>
                <span className={styles.actions}>
                  <button
                    className={styles.iconBtn}
                    title="Edit list"
                    onClick={() => setEditing({ list })}
                  >
                    <Icon name="pencil" size={15} />
                  </button>
                  <button
                    className={styles.iconBtn}
                    title="Delete list"
                    onClick={() => remove(list.id)}
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
